import styled from 'styled-components';
import React, { useEffect, useState } from 'react';
import { Info } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert, ContentLoader, palette, TertiaryButton, Tooltip, Typography as T } from '@dev/ee-components';
import { Link } from '@material-ui/core';
import { push as Push } from 'redux-first-history';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom';

import { safelyEncodeBase64Url } from '@dev/ee-tools';
import useMobile from '@/services/hooks/useMobile';
import dayjs from '@/services/tools/dayjs';
import {
    ContactGetGrowthStart,
    ContactsOverviewStartLoading,
    getContactsOverviewStore,
} from '@/store/contacts/actions';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { formatNumber } from '@/services/tools/formatNumber';
import { getConfigStore } from '@/store/config/actions';

import { ALL_CONTACTS_SEGMENT } from '@/services/constants';
import { StyledProps } from './ContactOverview';
import { AudiencePerformance } from './AudiencePerformance';
import { ContactsGrowthGraph } from './ContactsGrowthGraph';
import { ContactsGrowth } from './ContactsGrowth';

const ScreenContainer = styled.div<StyledProps>`
    & .contentWrapper {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
    }
    & .board {
        background: ${palette.basic.paper};
        border-radius: 4px;
        border: 1px solid ${palette.gray[3]};
        margin: 0 15px 32px 15px;
        &:last-child {
            margin-bottom: 0;
        }
        & .boardsection + .boardsection {
            border-top: 1px solid ${palette.gray[3]};
        }

        & .halfsection {
            display: inline-block;
            margin-right: 48px;

            & .subscribetooltip {
                & div {
                    display: flex;
                }
            }
            svg {
                fill: #babcff;
                margin-left: 8px;
            }
        }

        & .boardsection {
            padding: 16px 32px;
            &.verticalpadding {
                padding-top: 25px;
                padding-bottom: 25px;
            }
            &.widesection {
                padding: 30px 32px;
            }
            &.nohorizontalpadding {
                padding-left: 0;
                padding-right: 0;
            }
            a {
                color: ${palette.indigo[6]};
                font-weight: 600;
                font-size: inherit;
            }

            & .panelContent {
                display: flex;
                line-height: 2.4rem;
                align-items: center;
                justify-content: space-between;
                padding-bottom: 8px;
                svg {
                    fill: #babcff;
                }
            }
        }
        & .header > span,
        & .header > div:not(.right) {
            line-height: ${({ isMobile }): number => (isMobile ? 20 : 40)}px;
        }
        & .boardsection.split > div,
        & .boardsection.split > span {
            width: 50%;
            display: inline-block;
        }
        & .boardsection.split > div:last-of-type {
            text-align: right;
        }
    }
    .graphicboard {
        background-color: #eaecfb;
        height: 168px;
        padding-right: 33%;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .overviewimagecontainer {
        position: absolute;
        right: 0;
        bottom: 0;

        & svg {
            display: flex;
        }
    }
`;

const NarrowColumn = styled.div<StyledProps>`
    margin-top: 32px;
    flex: 0.4;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    & .board {
        margin-left: 0;
    }
    ${({ isMobile }): string =>
        isMobile &&
        `
    flex: 1;
    & .board{
        margin-left:0;
        margin-right:0;
    }
    `}
`;
const WideColumn = styled.div<StyledProps>`
    margin-top: 32px;
    flex: 0.6;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    & .board {
        margin-right: 0;
    }
    ${({ isMobile }): string =>
        isMobile &&
        `
    flex-basis:100%;
    max-width:100%;
    & .board{
        margin-left:0;
        margin-right:0 !important;
        width: 100%;
    }
    `}
`;

const AlertTextPart = styled.div`
    & div {
        display: inline-block;
        padding-right: 1px;
    }
`;

const TooltipWrapper = styled.div`
    & .MuiTooltip-popper {
        max-width: 250px;
    }
`;

export const SubscribedTooltip = (id: string, title: string): JSX.Element => (
    <TooltipWrapper>
        <Tooltip
            id={id}
            title={title}
            color={'#fff'}
            placement="top"
            interactive
            PopperProps={{ keepMounted: false, disablePortal: true }}
        >
            <Info fontSize="small" />
        </Tooltip>
    </TooltipWrapper>
);

const RenderRecentGrowth = (): JSX.Element => {
    const { t } = useTranslation();
    const contactsOverview = useSelector(getContactsOverviewStore);

    return (
        <div className="board">
            <div className="boardsection">
                <T variant="h2">{t('recentgrowth')}</T>
                <T variant="body1">{t('recentgrowthdesc')}</T>
            </div>
            <div className="boardsection split">
                <T variant="body1">{t('newcontacts')}</T>
                {!contactsOverview && (
                    <T variant="body1">
                        <ContentLoader count={1} mode="row" width={'60px'} />
                    </T>
                )}
                {contactsOverview && (
                    <T variant="h2">
                        {formatNumber(
                            contactsOverview.NewContactsLast30Days - contactsOverview.UnsubscribedLast30Days
                        ) || 0}
                    </T>
                )}
            </div>
            <div className="boardsection split">
                <T variant="body1">{t('unsubscribed')}</T>
                {!contactsOverview && (
                    <T variant="body1">
                        <ContentLoader count={1} mode="row" width={'60px'} />
                    </T>
                )}
                {contactsOverview && <T variant="h2">{formatNumber(contactsOverview?.UnsubscribedLast30Days) || 0}</T>}
            </div>
        </div>
    );
};

const RenderSummary = (): JSX.Element => {
    const { t } = useTranslation();
    const contactsOverview = useSelector(getContactsOverviewStore);
    const dispatch = useDispatch();

    return (
        <div className="board">
            <div className="boardsection header">
                <T variant="h2">{t('summary')}</T>
            </div>
            <div className="boardsection">
                <div className="halfsection">
                    {contactsOverview && (
                        <T variant="h1" style={{ paddingBottom: '2px' }}>
                            {formatNumber(contactsOverview?.TotalContacts) || 0}
                        </T>
                    )}
                    {!contactsOverview && <ContentLoader count={1} mode="row" width={'60px'} />}
                    <T variant="body1">
                        <TertiaryButton
                            onClick={() =>
                                dispatch(
                                    Push(
                                        ContactsPaths.SingleSegment.replace(
                                            ':segmentname',
                                            safelyEncodeBase64Url(ALL_CONTACTS_SEGMENT)
                                        )
                                    )
                                )
                            }
                            text={t('allcontacts')}
                        />
                    </T>
                </div>
                <div className="halfsection">
                    {contactsOverview && (
                        <T variant="h2" style={{ paddingBottom: '6px' }}>
                            {formatNumber(
                                (contactsOverview?.ActiveContacts || 0) + (contactsOverview?.EngagedContacts || 0)
                            ) || 0}
                        </T>
                    )}
                    {!contactsOverview && <ContentLoader count={1} mode="row" width={'60px'} />}
                    <T variant="body1" className="subscribetooltip">
                        {t('subscribed')} {SubscribedTooltip('subscribed', t('subscribedtooltip'))}{' '}
                    </T>
                </div>
            </div>
            <div className="boardsection split">
                <T variant="body1">{t('newcontactstoday')}</T>
                {!contactsOverview && (
                    <T variant="h2">
                        <ContentLoader count={1} mode="row" width={'60px'} />
                    </T>
                )}
                {contactsOverview && (
                    <T variant="h2">
                        {(contactsOverview?.NewContactsToday || 0) > 0
                            ? formatNumber(contactsOverview?.NewContactsToday)
                            : 0}
                    </T>
                )}
            </div>
        </div>
    );
};

const OverviewAlert = (contactoverviewalertKey, takemebackKey, hereKey): JSX.Element => (
    <AlertTextPart>
        <T variant="body1">
            {contactoverviewalertKey} {takemebackKey}
            <Link href="#/contacts">{hereKey}.</Link>
        </T>
    </AlertTextPart>
);

export const ContactOverview = (): JSX.Element => {
    const config = useSelector(getConfigStore);
    const isMobile = useMobile();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [contactsGrowthFrom, setContactsGrowthFrom] = useState('');
    const isNextGenDash = config?.isStandalone || config?.bootedAsChild;
    const location = useLocation();
    const query = parse(location.search);
    const startDate = query?.from?.toString();
    const endDate = query?.to?.toString();

    useEffect(() => {
        const To = endDate ? endDate : dayjs().utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const From = startDate
            ? startDate
            : dayjs().startOf('day').utc().subtract(6, 'day').format('YYYY-MM-DDTHH:mm:ss');
        setContactsGrowthFrom(From);
        dispatch(ContactsOverviewStartLoading({ From, To }));
        dispatch(ContactGetGrowthStart(From, To));
    }, []);

    return (
        <ScreenContainer isMobile={isMobile}>
            <div className="contentWrapper">
                {!isNextGenDash && (
                    <div style={{ width: '100%', marginTop: '25px' }}>
                        <Alert
                            severity="info"
                            body={OverviewAlert(t('contactoverviewalert'), t('takemeback'), t('here'))}
                        />
                    </div>
                )}
                <NarrowColumn isMobile={isMobile}>
                    <RenderSummary />
                    <RenderRecentGrowth />
                    <AudiencePerformance setContactsGrowthFrom={setContactsGrowthFrom} />
                </NarrowColumn>
                <WideColumn isMobile={isMobile}>
                    <ContactsGrowthGraph
                        contactsGrowthFrom={contactsGrowthFrom}
                        setContactsGrowthFrom={setContactsGrowthFrom}
                    />
                    <ContactsGrowth />
                </WideColumn>
            </div>
        </ScreenContainer>
    );
};

export default ContactOverview;
